import { createAsyncThunk } from '@reduxjs/toolkit';
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig';
import { showSuccessToast, showInfoToast } from '../../../../utils/toastUtils';
import { getErrorMessage } from '../utils.js';

// Submit waitlist entry
export const submitWaitlistEntry = createAsyncThunk(
  'waitlist/submit',
  async (entryData, { rejectWithValue, getState }) => {
    try {
      showInfoToast('Joining waitlist...');

      // First check if email already exists
      const waitlistRef = collection(db, 'waitlist_entries');
      const emailQuery = query(waitlistRef, where('email', '==', entryData.email.toLowerCase()));
      const existingEntries = await getDocs(emailQuery);

      if (!existingEntries.empty) {
        throw new Error('This email is already on the waitlist.');
      }

      // Decide which path will send the confirmation email (client Netlify vs Cloud Function)
      const emailEndpoint = process.env.REACT_APP_WAITLIST_EMAIL_ENDPOINT;

      // Prepare entry data
      const waitlistEntry = {
        email: entryData.email.toLowerCase(),
        firstName: entryData.firstName.trim(),
        lastName: entryData.lastName?.trim() || '',
        status: 'pending', // pending, approved, rejected
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailSendPath: emailEndpoint ? 'client-netlify' : 'cloud-function'
      };

      // Add to Firestore
      const docRef = await addDoc(waitlistRef, waitlistEntry);

      // Trigger confirmation email via Netlify Function if configured
      try {
        if (emailEndpoint) {
          fetch(emailEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            mode: 'cors',
            body: JSON.stringify({
              email: waitlistEntry.email,
              firstName: waitlistEntry.firstName
            })
          }).catch((e) => console.warn('Email endpoint request failed', e));
        }
      } catch (emailErr) {
        console.warn('Error triggering confirmation email:', emailErr);
      }

      // Predict updated waitlist count locally to avoid extra read
      const state = getState().waitlist;
      const prevCount = typeof state.waitlistCount === 'number'
        ? state.waitlistCount
        : (typeof state.stats?.totalCount === 'number' ? state.stats.totalCount : null);
      const waitlistCount = prevCount != null ? prevCount + 1 : null;

      showSuccessToast('Successfully joined the waitlist!');

      return {
        id: docRef.id,
        ...waitlistEntry,
        waitlistCount
      };
    } catch (error) {
      console.error('Error submitting waitlist entry:', error);
      const errorMessage = getErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);
